'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useFeaturePreviewEligibility from '@alltrails/core/hooks/useFeaturePreviewEligibility';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import TrailWeatherOverviewBackgroundTheme from '@alltrails/conditions/types/TrailWeatherOverviewBackgroundTheme';
import { ConditionStyleId } from '@alltrails/maps';
import IconButton from '@alltrails/denali/components/IconButton';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import { useReducedMotion } from '@alltrails/core';
import PagedProgressBar from '@alltrails/denali/components/PagedProgressBar';
import dynamic from 'next/dynamic';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import logTrailDetailsConditionsCTATapped from '@alltrails/amplitude/events/logTrailDetailsConditionsCTATapped';
import { useExperiments } from '@alltrails/experiments';
import useUser from '@alltrails/context/hooks/useUser';
import { updateIsConditionsPreviewActive } from '@alltrails/conditions/redux/conditionsSlice';
import TrailConditionsWeatherMapButton from '../TrailConditionsWeatherMapButton/TrailConditionsWeatherMapButton';
import styles from './TrailConditionsUpsellCard.module.scss';
import { lottieDurationMs } from './constants';
import { useDispatch } from '@/utils/redux';
import { setIsTrailMapOpen } from '@/utils/trailPageSlice';
import { getTrailConditionsScreen } from '@/utils/conditionsAmplitudeHelpers';

const StaticImage = dynamic(() => import('./StaticImage'), { ssr: false });
const UpsellLottie = dynamic(() => import('./UpsellLottie'), { ssr: false });

type TrailConditionsUpsellCardProps = {
  initialTheme: TrailWeatherOverviewBackgroundTheme;
  trailId: number;
  onShowUpsellModal: () => void;
};

const TrailConditionsUpsellCard = ({ initialTheme, trailId, onShowUpsellModal }: TrailConditionsUpsellCardProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isMobile = useIsMobileSizedScreen();
  const experiments = useExperiments();

  const containerRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(containerRef);
  const prefersReducedMotion = useReducedMotion();
  const [showLottie, setShowLottie] = useState(false);

  useEffect(() => {
    if (isVisible && !prefersReducedMotion) {
      setShowLottie(true);
    }
  }, [isVisible, prefersReducedMotion]);

  const showGroundConditions = experiments['web-disco-rm-ground-conditions']?.value === 'treatment';
  const conditions = useMemo(() => {
    let conditionOrder: ConditionStyleId[] = ['temperature', 'precipitation', 'snowDepth'];
    if (initialTheme.includes('rainy')) {
      conditionOrder = ['precipitation', 'temperature', 'snowDepth'];
    }
    if (showGroundConditions) {
      conditionOrder.push('groundConditions');
    }
    return conditionOrder;
  }, [initialTheme, showGroundConditions]);

  const [activeIndex, setActiveIndex] = useState(0);
  const activeCondition = conditions[activeIndex];

  const advanceCondition = useCallback(() => {
    setActiveIndex(i => (i + 1) % conditions.length);
  }, [conditions.length]);

  const title = useMemo(() => {
    switch (activeCondition) {
      case 'groundConditions':
        return intl.formatMessage({ defaultMessage: 'Is it muddy? Icy? Dry?' });
      case 'precipitation':
        return intl.formatMessage({ defaultMessage: 'When and where will it rain?' });
      case 'snowDepth':
        return intl.formatMessage({ defaultMessage: 'Is there snow on the trail?' });
      case 'temperature':
        return intl.formatMessage({ defaultMessage: 'Where will it be warmest?' });
      default:
        return '';
    }
  }, [activeCondition, intl]);

  const user = useUser();
  const { isEligible, markIneligible } = useFeaturePreviewEligibility('advanced_conditions', user);

  const onMapButtonClick = useCallback(() => {
    logTrailDetailsConditionsCTATapped({
      action_taken: 'cta tapped',
      conditions_screen: getTrailConditionsScreen(activeCondition),
      cta_index: activeIndex,
      cta_type: 'weather map',
      trail_id: trailId
    });

    if (isMobile) {
      onShowUpsellModal();
    } else {
      if (isEligible) {
        markIneligible();
        dispatch(setIsTrailMapOpen(true));
        dispatch(updateIsConditionsPreviewActive(true));
        dispatch(updateDynamicStyleIds([activeCondition]));
      } else {
        onShowUpsellModal();
      }
    }
  }, [activeCondition, activeIndex, dispatch, isEligible, isMobile, markIneligible, onShowUpsellModal, trailId]);

  const contents = (
    <>
      <PagedProgressBar
        className={styles.progressBar}
        index={activeIndex}
        total={conditions.length}
        stepDurationMs={showLottie ? lottieDurationMs : 0}
      />
      <div className={styles.heading}>
        <FormattedMessage defaultMessage="Trail conditions" />

        {experiments['rm-conditions-plus']?.value === 'on' ? (
          <PlusLogoBadge size={isMobile ? 'sm' : 'md'} textColor="dark" variant="full" />
        ) : (
          <PeakLogoBadge size={isMobile ? 'sm' : 'md'} textColor="dark" variant="full" />
        )}
      </div>
      {showLottie ? <UpsellLottie condition={activeCondition} onEnd={advanceCondition} /> : <StaticImage condition={activeCondition} />}
      <div className={styles.title}>{title}</div>
      <div className={styles.description}>{intl.formatMessage({ defaultMessage: 'Get hourly trail conditions for the week ahead' })}</div>
    </>
  );

  return (
    <div className={classNames(styles.container, styles[activeCondition])} ref={containerRef}>
      {isMobile ? (
        <button aria-label={intl.formatMessage({ defaultMessage: 'Next' })} className={styles.content} onClick={advanceCondition}>
          {contents}
        </button>
      ) : (
        <div className={styles.content}>
          {contents}
          <IconButton
            className={styles.nextButton}
            icon={{ Component: ArrowSq, orientation: 'right' }}
            onClick={advanceCondition}
            title={intl.formatMessage({ defaultMessage: 'Next' })}
            testId="next-condition-button"
            size="md"
          />
        </div>
      )}
      <TrailConditionsWeatherMapButton className={styles.mapButton} condition={conditions[activeIndex]} onClick={onMapButtonClick} />
    </div>
  );
};

export default TrailConditionsUpsellCard;

export const TrailConditionsUpsellCardLoading = () => {
  return <ShimmerContainer className={classNames(styles.container, styles.loading)} />;
};
